// =============================================================================
// bubbles/TextBubble.tsx — Bolha de mensagem de texto simples.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../../../lib/cn';
import type { Message } from '../../../types';
import { StatusIcon } from '../StatusIcon';
import { formatBubbleTime } from '../utils';
import { parseWhatsappMarkup } from '../whatsappMarkup';
import type { MarkupStyle } from '../whatsappMarkup';

interface TextBubbleProps {
  message: Message;
  isOutbound: boolean;
}

/** Classes do DS por estilo — sem cor/peso hard-coded. */
const STYLE_CLASS: Readonly<Record<MarkupStyle, string>> = {
  bold: 'font-semibold',
  italic: 'italic',
  strike: 'line-through',
  mono: 'font-mono text-[0.9em]',
};

/**
 * Renderiza o conteúdo com a formatação do WhatsApp (F16-S56).
 *
 * Monta nós React a partir dos tokens — nunca `dangerouslySetInnerHTML`. O texto
 * vem do cidadão e é entrada não confiável; o React escapa cada trecho.
 */
function FormattedContent({ content }: { content: string | null }): React.JSX.Element {
  const tokens = parseWhatsappMarkup(content ?? '');

  return (
    <>
      {/* Índice como key é seguro aqui: os tokens são posicionais, derivados só
          do conteúdo, sem estado próprio — conteúdo novo re-renderiza a lista toda. */}
      {tokens.map((token, i) =>
        token.styles.length === 0 ? (
          <React.Fragment key={i}>{token.text}</React.Fragment>
        ) : (
          <span key={i} className={cn(token.styles.map((s) => STYLE_CLASS[s]))}>
            {token.text}
          </span>
        ),
      )}
    </>
  );
}

export function TextBubble({ message, isOutbound }: TextBubbleProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'relative rounded-md px-3 py-2 max-w-full',
        'font-sans text-sm leading-relaxed',
        isOutbound
          ? [
              // Outbound: azul suave (color-mix não tem suporte universal, usa azul-light/10)
              'self-end',
              'bg-[color-mix(in_srgb,var(--brand-azul)_12%,var(--bg-elev-1))]',
              'text-ink',
              'border border-[color-mix(in_srgb,var(--brand-azul)_20%,transparent)]',
              '[box-shadow:var(--elev-1)]',
              'rounded-br-xs',
            ]
          : [
              // Inbound: surface-subtle
              'self-start',
              'bg-surface-2',
              'text-ink border border-border-subtle',
              '[box-shadow:var(--elev-1)]',
              'rounded-bl-xs',
            ],
      )}
    >
      {/* Conteúdo — whitespace-pre-wrap preserva quebras de linha */}
      <p className="whitespace-pre-wrap break-words">
        <FormattedContent content={message.content} />
      </p>

      {/* Rodapé: timestamp + status */}
      <div
        className={cn('flex items-center gap-1 mt-1', isOutbound ? 'justify-end' : 'justify-start')}
      >
        <time
          dateTime={message.createdAt}
          className="font-sans text-xs text-ink-3 tabular-nums"
          title={new Date(message.createdAt).toLocaleString('pt-BR')}
        >
          {formatBubbleTime(message.createdAt)}
        </time>
        {isOutbound && message.viewStatus && <StatusIcon status={message.viewStatus} size={12} />}
      </div>
    </div>
  );
}
